// Styles
// Import Flag Icons Set
// import 'flag-icon-css/css/flag-icon.min.css';
// Import Font Awesome Icons Set
// import 'font-awesome/css/font-awesome.min.css';
// Import Simple Line Icons Set
// import 'simple-line-icons/css/simple-line-icons.css';
// Import Main styles for this application
import "./utils/objectPolyfill";
import "./scss/style.scss";
// Temp fix for reactstrap
import "./scss/core/_dropdown-menu-right.scss";
import "react-select/scss/default.scss";
import "medium-draft/lib/basic.css";
import "./scss/mps-styles.scss";

import locationHelperBuilder from "redux-auth-wrapper/history4/locationHelper";
import { Route, Router, Switch } from "react-router-dom";
import { persistor, store } from "./State/store/configureStore";
import I18nProviderWrapper from "./components/I18nProviderWrapper";
import asyncComponent from "./components/AsyncComponent";
import Login from "./Common/Login/";
import EntraIdCallback from "./Common/Login/EntraIdCallback";
import EntraIdFacilitySelector from "./Common/Login/EntraIdFacilitySelector";
import PublicChannelLogin from "./Common/Login/PublicChannelLogin";
import ChannelSignIn from "./Common/Login/ChannelSignIn";
import Onboarding from "./Common/Onboarding/";
import UserOnboarding from "./Common/UserOnboarding/";
import { PersistGate } from "redux-persist/integration/react";
import { Provider } from "react-redux";
import React from "react";
import ReactDOM from "react-dom";
import { connectedRouterRedirect } from "redux-auth-wrapper/history4/redirect";
import history from "./history";
import ForgotPasswordView from "./Common/ForgotPassword/ForgotPasswordView";
import Toaster from "./components/Alerts/Toaster";
import ResetPasswordView from "./Common/ResetPassword/ResetPasswordView";
import SignupContainer from "./Common/Signup/SignupContainer";
import {
  isFacilityAdmin,
  isMpsAdmin,
  isMpsSupport,
  isPatientOrMember,
  isStaff,
  ROLE_CROSS_FACILITY_HSE_VIEWER,
} from "./security/roles";
import UnsubscribeContainer from "./Common/Notifications/UnsubscribeContainer";
import isIOSChromeOrSafari from "./components/VideoCall/Utils";
import UnsupportedVideoClients from "./components/VideoCall/UnsupportedVideoClients";
import NewAppointmentContainer from "./PatientApp/containers/NewAppointmentContainer";
import GroupClassAppointmentContainer from "./PatientApp/containers/GroupClassAppointmentContainer";
import CancelAppointmentContainer from "./PatientApp/containers/CancelAppointmentContainer";
import PatientsSearch from "./StaffApp/Patients/PatientsSearch/PatientsSearch";
import AnonymousVideoCallContainer from "./components/VideoCall/AnonymousVideoCallContainer";
import AnonymousSubmitAssessmentsContainer from "./PatientApp/components/Assessments/AnonymousAppointmentScreeningContainer";
import I18nProvider from "./services/I18nProvider";
import VideoCallViewForLinks from "./components/VideoCall/VideoCallViewForLinks";
import Demo from "./demo";
import Home from "./public/Home";
import AgentChat from "./Shared/AgentChat";
import DownloadApp from "./components/DownloadApp";
import Text from "./components/Text";
import Messages from "./Shared/Messages";

const AsyncFull = asyncComponent(() => import("./containers/Full/"));
const AsyncPatientAppLayout = asyncComponent(() =>
  import("./PatientApp/components/PatientAppLayout")
);

const userIsAuthenticated = connectedRouterRedirect({
  // The url to redirect user to if they fail
  redirectPath: "/public",
  // If selector is true, wrapper will not redirect
  // For example let's check that state contains user data
  authenticatedSelector: state => state.user.loggedIn,
  // A nice display name for this check
  wrapperDisplayName: "UserIsAuthenticated",
  allowRedirectBack: false
});

const userIsAuthenticatedForVideo = connectedRouterRedirect({
  redirectPath: "/public",
  authenticatedSelector: state => state.user.loggedIn,
  wrapperDisplayName: "UserIsAuthenticated",
  allowRedirectBack: (nextState, redirectPath) => {
    return true;
  }
});

const locationHelper = locationHelperBuilder({});
const userIsNotAuthenticated = connectedRouterRedirect({
  // This sends the user either to the query param route if we have one, or to the landing page if none is specified and the user is already logged in
  // redirectPath: (state, ownProps) =>
  //   isPatientOrMember(state.user.role) ? "/app" : "/",
  redirectPath: (state, ownProps) => {
    let param = locationHelper.getRedirectQueryParam(ownProps);
    if (param && !(isPatientOrMember(state.user.role) && param.startsWith("/patients/"))) {
      return param;
    }
    // Special redirect for MPS Support role directly to mps-patients
    if (isMpsSupport(state.user.role)) {
      return "/mps-patients";
    }
    return isPatientOrMember(state.user.role) ? "/app" : "/";
  },

  // This prevents us from adding the query parameter when we send the user away from the login page
  allowRedirectBack: false,
  // If selector is true, wrapper will not redirect
  // So if there is no user data, then we show the page
  authenticatedSelector: state => !state.user.loggedIn,
  // A nice display name for this check
  wrapperDisplayName: "UserIsNotAuthenticated"
});

const userIsPatient = connectedRouterRedirect({
  redirectPath: "/public",
  authenticatedSelector: state => {
    return state.user.loggedIn && isPatientOrMember(state.user.role);
  },
  wrapperDisplayName: "User Is Patient",
  allowRedirectBack: false
});

const userIsNotPatient = connectedRouterRedirect({
  redirectPath: "/public",
  authenticatedSelector: state =>
    state.user.loggedIn &&
    (isStaff(state.user.role) ||
      isFacilityAdmin(state.user.role) ||
      isMpsAdmin(state.user.role) ||
      isMpsSupport(state.user.role) ||
      state.user.role === ROLE_CROSS_FACILITY_HSE_VIEWER),
  wrapperDisplayName: "User Is Not Patient",
  // Preserve the destination for staff deep links (e.g. _PatientLink/_TaskLink
  // email links) so login returns the user to the page they asked for.
  allowRedirectBack: (nextState, redirectPath) =>
    !!(nextState.location && nextState.location.pathname.startsWith("/patients/"))
});

// Environment info logged only in development (devLog suppressed in production)

if (window.location.pathname.startsWith("/app/external-link")) {
  sessionStorage.setItem("externalLinkRedirect", window.location.pathname + window.location.search);
}

let isRTL = I18nProvider.isRTLLocale();

// WCAG: make <a> elements without href keyboard-focusable.
// The codebase uses <a onClick={...}> without href as clickable elements;
// browsers don't include these in the tab order by default.
// Also watches for aria-hidden changes and removes tabindex from affected elements.
(function patchAnchorsFocusability() {
  const PATCHED = "data-wcag-patched";

  function patch(el) {
    if (el.tagName === "A" && !el.hasAttribute("href") && !el.hasAttribute(PATCHED)) {
      if (el.closest('[aria-hidden="true"]')) return;
      el.setAttribute("tabindex", "0");
      el.setAttribute("role", "button");
      el.setAttribute(PATCHED, "1");
    }
  }

  function unpatchHidden(root) {
    root.querySelectorAll('a[' + PATCHED + ']').forEach(function(el) {
      if (el.closest('[aria-hidden="true"]')) {
        el.removeAttribute("tabindex");
        el.removeAttribute(PATCHED);
      }
    });
  }

  new MutationObserver(function(mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var m = mutations[i];
      // New nodes added — patch anchors
      for (var j = 0; j < m.addedNodes.length; j++) {
        var node = m.addedNodes[j];
        if (node.nodeType !== 1) continue;
        patch(node);
        if (node.querySelectorAll) node.querySelectorAll("a:not([href]):not([" + PATCHED + "])").forEach(patch);
      }
      // Attribute changed — if aria-hidden was set, unpatch descendants
      if (m.type === "attributes" && m.attributeName === "aria-hidden") {
        var target = m.target;
        if (target.getAttribute("aria-hidden") === "true") {
          unpatchHidden(target);
        } else {
          // aria-hidden removed — re-patch anchors inside
          target.querySelectorAll("a:not([href]):not([" + PATCHED + "])").forEach(patch);
        }
      }
    }
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["aria-hidden"] });
  document.querySelectorAll("a:not([href]):not([" + PATCHED + "])").forEach(patch);
})();

// WCAG: auto-label icon-only interactive elements.
// Many buttons/links contain only an <i> icon with no text or aria-label.
(function patchIconOnlyElements() {
  const ICON_CLASS_MAP = {
    "fa-edit": "Edit", "fa-pencil": "Edit", "fa-pen": "Edit",
    "fa-trash": "Delete", "fa-trash-alt": "Delete", "fa-times": "Close",
    "fa-plus": "Add", "fa-plus-circle": "Add", "fa-minus": "Remove",
    "fa-eye": "View", "fa-eye-slash": "Hide",
    "fa-search": "Search", "fa-filter": "Filter",
    "fa-download": "Download", "fa-upload": "Upload",
    "fa-copy": "Copy", "fa-clone": "Clone",
    "fa-cog": "Settings", "fa-cogs": "Settings",
    "fa-chevron-left": "Previous", "fa-chevron-right": "Next",
    "fa-chevron-up": "Expand", "fa-chevron-down": "Collapse",
    "fa-arrow-left": "Back", "fa-arrow-right": "Forward",
    "fa-check": "Confirm", "fa-save": "Save",
    "fa-undo": "Undo", "fa-redo": "Redo",
    "fa-share": "Share", "fa-share-alt": "Share",
    "fa-info": "Info", "fa-info-circle": "Info",
    "fa-exclamation": "Warning", "fa-exclamation-triangle": "Warning",
    "fa-bell": "Notifications", "fa-envelope": "Message",
    "fa-print": "Print", "fa-file": "File",
    "fa-link": "Link", "fa-external-link-alt": "Open link",
    "fa-sync": "Refresh", "fa-refresh": "Refresh",
    "fa-ellipsis-v": "More options", "fa-ellipsis-h": "More options",
    "fa-bars": "Menu", "fa-grip-vertical": "Drag",
    "fa-lock": "Lock", "fa-unlock": "Unlock",
    "fa-user": "User", "fa-users": "Users",
    "fa-calendar": "Calendar", "fa-clock": "Time",
    "fa-star": "Favourite", "fa-heart": "Favourite",
    "fa-comment": "Comment", "fa-comments": "Comments",
    "fa-paperclip": "Attach", "fa-expand": "Fullscreen",
    "fa-compress": "Exit fullscreen", "fa-sort": "Sort",
    "fa-sort-up": "Sort ascending", "fa-sort-down": "Sort descending",
  };

  function labelFromIcon(iconEl) {
    for (const cls of iconEl.classList) {
      if (ICON_CLASS_MAP[cls]) return ICON_CLASS_MAP[cls];
    }
    return null;
  }

  function patchEl(el) {
    if (el.getAttribute("aria-label") || el.getAttribute("aria-labelledby")) return;
    if (el.getAttribute("title")) return;
    if (el.closest('[aria-hidden="true"]')) return;
    const text = el.textContent.trim();
    if (text.length > 0) return;

    // Try FA icon class
    const icon = el.querySelector("i[class*='fa-'], span[class*='fa-']");
    if (icon) {
      const label = labelFromIcon(icon);
      if (label) { el.setAttribute("aria-label", label); return; }
    }

    // Try child element with title
    const titledChild = el.querySelector("[title]");
    if (titledChild) { el.setAttribute("aria-label", titledChild.getAttribute("title")); return; }

    // Try SVG with <title> child
    const svg = el.querySelector("svg");
    if (svg) {
      const svgTitle = svg.querySelector("title");
      if (svgTitle && svgTitle.textContent.trim()) { el.setAttribute("aria-label", svgTitle.textContent.trim()); return; }
    }

    // Try deriving from the element's own CSS classes
    const classLabel = {
      "btn-close": "Close", "close": "Close", "navbar-toggler": "Menu",
      "sidebar-toggler": "Toggle sidebar", "sidebar-minimizer": "Minimize sidebar",
      "dropdown-toggle": "Menu",
    };
    for (const [cls, lbl] of Object.entries(classLabel)) {
      if (el.classList.contains(cls)) { el.setAttribute("aria-label", lbl); return; }
    }

    // Fallback: label as "Button" to prevent critical violation
    el.setAttribute("aria-label", "Action");
  }

  function patchImg(img) {
    // Hide images with no/empty src — they show broken image + alt text otherwise
    var src = img.getAttribute("src") || "";
    if (!src || src === "null" || src === "undefined") {
      img.style.display = "none";
      if (!img.hasAttribute("alt")) img.setAttribute("alt", "");
      return;
    }
    // Hide broken images on load error — prevents broken icon + alt text showing
    if (!img.dataset.wcagErrorHandled) {
      img.dataset.wcagErrorHandled = "1";
      img.addEventListener("error", function() { this.style.display = "none"; });
    }
    if (img.hasAttribute("alt")) return;
    if (img.getAttribute("role") === "presentation" || img.getAttribute("role") === "none") return;
    if (src.includes("logo")) { img.setAttribute("alt", "Logo"); return; }
    if (src.includes("avatar") || img.closest(".avatar")) { img.setAttribute("alt", "User avatar"); return; }
    img.setAttribute("alt", "");
    img.setAttribute("role", "presentation");
  }

  function patchInput(el) {
    if (el.getAttribute("aria-label") || el.getAttribute("aria-labelledby")) return;
    if (el.getAttribute("title")) return;
    if (el.type === "hidden" || el.type === "submit") return;

    // Check for associated <label>
    var id = el.id;
    if (id && document.querySelector('label[for="' + id + '"]')) return;

    // Check for wrapping <label>
    if (el.closest("label")) return;

    // Derive label from placeholder
    if (el.placeholder && el.placeholder.trim()) {
      el.setAttribute("aria-label", el.placeholder.trim());
      return;
    }

    // Derive from name attribute
    if (el.name) {
      var label = el.name
        .replace(/([A-Z])/g, " $1")
        .replace(/[_-]/g, " ")
        .replace(/^\s+/, "")
        .trim();
      if (label) { el.setAttribute("aria-label", label.charAt(0).toUpperCase() + label.slice(1)); return; }
    }

    // Derive from type
    var typeLabels = {
      text: "Text input", email: "Email", tel: "Phone number",
      password: "Password", search: "Search", url: "URL",
      number: "Number", date: "Date", time: "Time",
      "datetime-local": "Date and time", month: "Month", week: "Week",
      file: "File upload", color: "Colour picker",
    };
    if (typeLabels[el.type]) { el.setAttribute("aria-label", typeLabels[el.type]); return; }

    // Check for nearby label text (previous sibling or parent text)
    var prev = el.previousElementSibling;
    if (prev && prev.textContent.trim().length > 0 && prev.textContent.trim().length < 50) {
      el.setAttribute("aria-label", prev.textContent.trim());
      return;
    }

    el.setAttribute("aria-label", "Input field");
  }

  function patchTable(table) {
    if (table.getAttribute("aria-label") || table.getAttribute("aria-labelledby")) return;
    if (table.querySelector("caption")) return;
    if (!table.querySelector("th")) return; // skip layout tables

    // Try nearby heading
    var prev = table.previousElementSibling;
    while (prev && prev.tagName === "BR") prev = prev.previousElementSibling;
    if (prev && /^H[1-6]$/.test(prev.tagName) && prev.textContent.trim()) {
      table.setAttribute("aria-label", prev.textContent.trim());
      return;
    }

    // Try parent card header
    var card = table.closest(".card, .mps-card, [class*='card']");
    if (card) {
      var header = card.querySelector(".card-header, .card-title, h3, h4, h5");
      if (header && header.textContent.trim()) {
        table.setAttribute("aria-label", header.textContent.trim());
        return;
      }
    }

    // Try page title
    var pageHeading = document.querySelector("h1, h2, h3");
    if (pageHeading && pageHeading.textContent.trim()) {
      table.setAttribute("aria-label", pageHeading.textContent.trim() + " data");
      return;
    }

    table.setAttribute("aria-label", "Data table");
  }

  function patchAll(root) {
    root.querySelectorAll("a:not([aria-label]), button:not([aria-label])").forEach(patchEl);
    root.querySelectorAll("img:not([alt])").forEach(patchImg);
    root.querySelectorAll("input:not([aria-label]):not([aria-labelledby]):not([title]), textarea:not([aria-label]):not([aria-labelledby]):not([title]), select:not([aria-label]):not([aria-labelledby]):not([title])").forEach(patchInput);
    root.querySelectorAll("table:not([aria-label]):not([aria-labelledby])").forEach(patchTable);
  }

  new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.tagName === "A" || node.tagName === "BUTTON") patchEl(node);
        if (node.tagName === "IMG") patchImg(node);
        if (node.tagName === "INPUT" || node.tagName === "TEXTAREA" || node.tagName === "SELECT") patchInput(node);
        if (node.tagName === "TABLE") patchTable(node);
        if (node.querySelectorAll) patchAll(node);
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
  patchAll(document.body);
})();

ReactDOM.render(
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <I18nProviderWrapper>
        <div
          dir={isRTL ? "rtl" : "ltr"}
          style={{ textAlign: isRTL ? "right" : "left" }}
        >
          <a href="#main-layout" className="skip-to-main">
            <Text content={Messages.skip_to_main_content} />
          </a>
          <Toaster />
          <Router history={history}>
            <Switch>
              <Route
                exact
                path="/a/s/:key"
                name="Anonymous Assessments"
                component={AnonymousSubmitAssessmentsContainer}
              />
              <Route
                exact
                path="/a/p/:patientId"
                name="Public Channel Link"
                component={PublicChannelLogin}
              />
              <Route
                exact
                path="/a/signin"
                name="Channel Sign In"
                component={ChannelSignIn}
              />
              <Route
                exact
                path="/e/s/:eskey"
                name="Anonymous Assessments"
                component={AnonymousSubmitAssessmentsContainer}
              />
              <Route
                path="/patients-search"
                name="Patients"
                component={userIsAuthenticated(PatientsSearch)}
              />

              <Route
                exact
                path="/login/:invited/:facilityName"
                name="Login Page"
                component={userIsNotAuthenticated(Login)}
              />

              <Route
                exact
                path="/login"
                name="Login Page"
                component={userIsNotAuthenticated(Login)}
              />

              <Route
                exact
                path="/auth-complete"
                name="Entra ID Callback"
                component={EntraIdCallback}
              />

              <Route
                exact
                path="/entra-select-facility"
                name="Entra ID Facility Selection"
                component={EntraIdFacilitySelector}
              />

              <Route
                exact
                path="/forgot-password"
                name="Forgot Password"
                component={ForgotPasswordView}
              />

              <Route
                exact
                path="/reset-password/:token"
                name="Forgot Password"
                component={ResetPasswordView}
              />

              <Route
                path="/invite/:inviteCode"
                name="Onboarding"
                component={Onboarding}
              />

              <Route
                path="/acceptTerms"
                name="Onboarding"
                component={Onboarding}
              />

              <Route
                path="/confirmation/:confirmationCode?"
                name="Onboarding"
                component={Onboarding}
              />

              <Route
                path="/setCredentials"
                name="Onboarding"
                component={userIsNotAuthenticated(Onboarding)}
              />

              <Route
                path="/credentials-present"
                name="Onboarding"
                component={userIsNotAuthenticated(Onboarding)}
              />

              <Route
                exact
                path="/unsubscribe/:code"
                name="Unsubscribe Page"
                component={userIsNotAuthenticated(UnsubscribeContainer)}
              />

              <Route
                path="/ob/:onboardingType/:inviteCode?"
                name="UserOnboarding"
                component={UserOnboarding}
              />

              <Route
                path="/signup/:code?"
                name="Self Sign Up"
                component={SignupContainer}
              />

              <Route
                path="/video-call/:roomName"
                name="Video calls"
                component={AnonymousVideoCallContainer}
              />

              <Route
                path="/fakeeh-care"
                name="Fakeeh Care"
                component={Demo}
              />

              <Route
                path="/download"
                name="Download App"
                component={DownloadApp}
              />

              <Route
                path="/public"
                name="Melanoma"
                component={Home}
              />
              <Route
                path="/melanoma"
                name="Melanoma"
                render={(props) => <Home {...props} test={true} />}
              />
              <Route
                path="/chat"
                name="Chat"
                component={AgentChat}
              />
              {/* <Route
                path="/e/chat"
                name="External Chat"
                component={AgentChat}
              /> */}

              {!isIOSChromeOrSafari() && (
                <Route
                  path="/join-video-call/:roomName"
                  name="Video calls"
                  component={userIsAuthenticatedForVideo(VideoCallViewForLinks)}
                />
              )}

              {isIOSChromeOrSafari() && (
                <Route
                  path="/join-video-call/:roomName"
                  name="Video calls"
                  component={UnsupportedVideoClients}
                />
              )}

              {/*<Route*/}
              {/*  path="/join-video-call-r/:roomName"*/}
              {/*  name="Video calls"*/}
              {/*  component={userIsAuthenticatedForVideo(ExistingRoomContainer)}*/}
              {/*/>*/}

              {/*<Route*/}
              {/*  path="/covid-19-dashboard"*/}
              {/*  name="Video calls"*/}
              {/*  component={Covid19StatsContainer}*/}
              {/*/>*/}

              <Route
                path="/appointments/:scheduleId/:templateId/book"
                name="New Appointment"
                component={NewAppointmentContainer}
              />

              <Route
                path="/appointments/:appointmentId/cancel"
                name="New Appointment"
                component={CancelAppointmentContainer}
              />

              <Route
                path="/group-classes/:scheduleId"
                name="New Appointment"
                component={GroupClassAppointmentContainer}
              />

              <Route
                path="/app"
                name="PatientHome"
                component={userIsPatient(AsyncPatientAppLayout)}
              />
              <Route
                path="/"
                name="Home"
                component={userIsNotPatient(AsyncFull)}
              />
            </Switch>
          </Router>
        </div>
      </I18nProviderWrapper>
    </PersistGate>
  </Provider>,
  document.getElementById("root")
);
